// Owner-only (permission 0) endpoints under /api/admin.
import { createApi } from "@reduxjs/toolkit/query/react";
import { createCustomFetchBaseQuery } from "../baseQuery";

// Drops empty filters so they don't end up as "?status=" in the URL.
const withParams = (path, params = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  ).toString();
  return query ? `${path}?${query}` : path;
};

export const adminApi = createApi({
  reducerPath: "adminApi",
  baseQuery: createCustomFetchBaseQuery(),
  tagTypes: ["AdminStats", "AdminUsers", "AdminTickets"],
  endpoints: (builder) => ({
    getAdminStats: builder.query({
      query: () => "/api/admin/stats",
      providesTags: ["AdminStats"],
    }),

    getAdminUsers: builder.query({
      query: (params) => withParams("/api/admin/users", params),
      providesTags: ["AdminUsers"],
    }),
    getAdminUser: builder.query({
      query: (id) => `/api/admin/users/${id}`,
      providesTags: (result, error, id) => [{ type: "AdminUsers", id }],
    }),
    updateAdminUser: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/api/admin/users/${id}`, method: "PATCH", body }),
      invalidatesTags: (result, error, { id }) => ["AdminUsers", { type: "AdminUsers", id }, "AdminStats"],
    }),

    getAdminMasjids: builder.query({
      query: (params) => withParams("/api/admin/masjids", params),
    }),
    getAdminWebsites: builder.query({
      query: (params) => withParams("/api/admin/websites", params),
    }),
    getAdminDevices: builder.query({
      query: (params) => withParams("/api/admin/devices", params),
    }),
    getAdminSubscriptions: builder.query({
      query: (params) => withParams("/api/admin/subscriptions", params),
    }),

    getAdminTickets: builder.query({
      query: (params) => withParams("/api/admin/tickets", params),
      providesTags: ["AdminTickets"],
    }),
    getAdminTicket: builder.query({
      query: (id) => `/api/admin/tickets/${id}`,
      providesTags: (result, error, id) => [{ type: "AdminTickets", id }],
    }),
    replyAdminTicket: builder.mutation({
      query: ({ id, message }) => ({ url: `/api/admin/tickets/${id}/replies`, method: "POST", body: { message } }),
      invalidatesTags: (result, error, { id }) => ["AdminTickets", { type: "AdminTickets", id }, "AdminStats"],
    }),
    updateAdminTicket: builder.mutation({
      query: ({ id, ...body }) => ({ url: `/api/admin/tickets/${id}`, method: "PATCH", body }),
      invalidatesTags: (result, error, { id }) => ["AdminTickets", { type: "AdminTickets", id }, "AdminStats"],
    }),
  }),
});

export const {
  useGetAdminStatsQuery,
  useGetAdminUsersQuery,
  useGetAdminUserQuery,
  useUpdateAdminUserMutation,
  useGetAdminMasjidsQuery,
  useGetAdminWebsitesQuery,
  useGetAdminDevicesQuery,
  useGetAdminSubscriptionsQuery,
  useGetAdminTicketsQuery,
  useGetAdminTicketQuery,
  useReplyAdminTicketMutation,
  useUpdateAdminTicketMutation,
} = adminApi;
